import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Transition } from 'react-transition-group';
import { isEmpty } from 'lodash';

import ConfirmDeleteModal from './Components/ConfirmDeleteModal';
import lookerApi from '../../../services/looker-api';

const duration = 200;

const defaultStyle = {
  transition: `all ${duration}ms ease-out`,
  transform: 'translateY(0)',
  opacity: 0
};

const transitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1, transform: 'translateY(30%)' }
};

export const Reports = ({ reports, userId, fetchReportList }) => {
  const [searchText, setSearchText] = useState('');
  const [showShareLink, setShowShareLink] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [toDelete, setToDelete] = useState('');

  const copyToClipboard = uuid => {
    const textField = document.createElement('textarea');
    textField.innerText = `${window.location.host}/reports/${uuid}`;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand('copy');
    textField.remove();
  };

  const handleClickShareLink = uuid => () => {
    copyToClipboard(uuid);
    setShowShareLink(true);
  };

  const toggleShowShareLink = () => {
    setShowShareLink(false);
  };

  const onChangeSearch = e => {
    setSearchText(e.target.value);
  };

  const onDeletePress = e => {
    e.preventDefault();
    setShowConfirmDeleteModal(true);
    setToDelete(e.currentTarget.value);
  };

  const handleCloseModal = () => {
    setToDelete('');
    setShowConfirmDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    const data = await lookerApi.deleteReport(toDelete, userId);
    if (isEmpty(data)) {
      /* error occurred */
    }
    await fetchReportList(userId);
    setShowConfirmDeleteModal(false);
  };

  return (
    <div className="user-profile__reports-wrapper">
      <ConfirmDeleteModal
        isOpen={showConfirmDeleteModal}
        onClose={handleCloseModal}
        onDelete={handleConfirmDelete}
        messageBody="Are you sure you want to delete this report? You cannot undo this action."
      />
      <div className="user-profile__reports-header">
        <div className="user-profile__reports-header-search">
          <FontAwesomeIcon className="search-icon" icon="search" size="1x" />
          <input
            onChange={onChangeSearch}
            placeholder="Search by Report Name..."
          />
        </div>
        <Link to="/report-builder">Create a new report </Link>
      </div>
      <div className="user-profile__reports-list">
        {reports
          .filter(
            s =>
              s.status !== 'disabled' &&
              s.name.toLowerCase().includes(searchText)
          )
          .map(s => (
            <div key={s.uuid} className="user-profile__reports-list-item">
              <div className="user-profile__reports-list-item-name">
                {s.name}
              </div>
              <Transition
                in={showShareLink}
                timeout={duration}
                unmountOnExit
                onEntered={() => setTimeout(() => toggleShowShareLink(), 2000)}
              >
                {state => (
                  <div
                    className="tvt-my-reports__modal-share-link"
                    style={{
                      ...defaultStyle,
                      ...transitionStyles[state]
                    }}
                  >
                    <i className="fa fa-check" aria-hidden="true" /> Link copied
                    to clipboard
                  </div>
                )}
              </Transition>
              <button
                onClick={handleClickShareLink(s.uuid)}
                className="user-profile__reports-list-item-share"
              >
                <FontAwesomeIcon icon="share-alt" size="1x" />
              </button>
              <Link
                to={`/reports/${s.uuid}`}
                className="user-profile__reports-list-item-edit"
              >
                <FontAwesomeIcon icon="pencil-alt" size="1x" />
              </Link>
              <button
                value={s.uuid}
                onClick={onDeletePress}
                className="user-profile__reports-list-item-delete"
              >
                <FontAwesomeIcon icon="trash-alt" size="1x" />
              </button>
            </div>
          ))}
      </div>
    </div>
  );
};

Reports.propTypes = {
  userId: PropTypes.string.isRequired,
  fetchReportList: PropTypes.func.isRequired,
  reports: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired
    })
  ).isRequired
};

export default Reports;
